import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import Swal from "sweetalert2";
import request from "../../../../Helpers/API";
import PlantillaScreen from "../../../helpers/PlantillaScreen2";
import { LoaderScreen } from "../../../components/LoaderScreen";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import DescriptionIcon from "@mui/icons-material/Description";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import CategoryIcon from "@mui/icons-material/Category";
import LabelIcon from "@mui/icons-material/Label";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

// Modal de Alta/Edición del catálogo Familia/Sub Familia por número de
// Parte (convención ram-modal). Props y contrato completo en README.md.
export const FamiliaRefaccionesModal = ({ open, registro, onClose, onGuardado }) => {
    const esEdicion = !!registro;

    const [catalogos, setCatalogos] = useState({
        partes: [],
        familias: [],
        subfamilias1: [],
        subfamilias2: [],
    });
    // Texto del campo "Parte" (freeSolo: catálogo o número nuevo).
    const [numeroParte, setNumeroParte] = useState("");
    const [nombreParte, setNombreParte] = useState("");
    const [linea, setLinea] = useState("");
    const [familia, setFamilia] = useState("");
    const [subFamilia1, setSubFamilia1] = useState("");
    const [subFamilia2, setSubFamilia2] = useState("");
    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    // Al abrir: precarga el registro (si es edición) y trae los catálogos.
    useEffect(() => {
        if (!open) return;

        setNumeroParte(registro?.numero_parte || "");
        setNombreParte(registro?.nombre_parte || "");
        setLinea(registro?.linea || "");
        setFamilia(registro?.familia || "");
        setSubFamilia1(registro?.subfamilia_1 || "");
        setSubFamilia2(registro?.subfamilia_2 || "");

        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .get("PartesFamilia/Catalogo")
            .then((result) => {
                setCatalogos({
                    partes: result?.partes || [],
                    familias: result?.familias || [],
                    subfamilias1: result?.subfamilias1 || [],
                    subfamilias2: result?.subfamilias2 || [],
                });
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire(
                    "FAMILIA DE REFACCIONES",
                    "No fue posible cargar los catálogos de Parte, Familia y Sub Familia. Intente nuevamente o contacte al administrador.",
                    "error"
                );
            });
    }, [open, registro]);

    if (!open) return null;

    // Al elegir una Parte del catálogo, autocompleta Nombre de Parte/Línea.
    const onSeleccionarParteDelCatalogo = (nuevaParte) => {
        if (!nuevaParte || typeof nuevaParte !== "object") return;
        setNumeroParte(nuevaParte.numero_parte || "");
        setNombreParte(nuevaParte.nombre_parte || "");
        setLinea(nuevaParte.linea || "");
    };

    // Los 6 campos son obligatorios; arreglo recorrible evita repetir el `if`.
    const CAMPOS_OBLIGATORIOS = [
        { valor: numeroParte, etiqueta: "el número de Parte" },
        { valor: nombreParte, etiqueta: "el Nombre de Parte" },
        { valor: linea, etiqueta: "la Línea" },
        { valor: familia, etiqueta: "la Familia" },
        { valor: subFamilia1, etiqueta: "la Sub Familia 1" },
        { valor: subFamilia2, etiqueta: "la Sub Familia 2" },
    ];

    const onGuardar = () => {
        const campoVacio = CAMPOS_OBLIGATORIOS.find((campo) => !campo.valor || !campo.valor.trim());
        if (campoVacio) {
            Swal.fire("FAMILIA DE REFACCIONES", `Indique ${campoVacio.etiqueta}.`, "warning");
            return;
        }

        const post = {
            idlistado: esEdicion ? registro.idlistado : -99,
            numero_parte: numeroParte.trim(),
            nombre_parte: nombreParte.trim(),
            linea: linea.trim(),
            familia: familia.trim(),
            subfamilia_1: subFamilia1.trim(),
            subfamilia_2: subFamilia2.trim(),
        };

        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .post("PartesFamilia/Guardar", post)
            .then(async () => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                await Swal.fire("FAMILIA DE REFACCIONES", "Registro guardado con éxito.", "success");
                onGuardado?.();
                onClose?.();
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire(
                    "FAMILIA DE REFACCIONES",
                    "No fue posible guardar el registro. Intente nuevamente o contacte al administrador.",
                    "error"
                );
            });
    };

    return (
        <div className="ram-backdrop">
            <PlantillaScreen
                titulo={esEdicion ? "Editar familia de refacciones" : "Agregar familia de refacciones"}
                className={"ram-modal"}
            >
                <button className="ram-close" onClick={onClose} type="button">
                    <CloseIcon />
                </button>
                <LoaderScreen loader={loader} />

                <div className="ram-body">
                    {/* Parte: freeSolo, catálogo o número nuevo. */}
                    <div className="ram-field">
                        <div className="ram-field__icon">
                            <SettingsInputComponentIcon />
                        </div>
                        <div className="ram-field__content">
                            <Autocomplete
                                size="small"
                                freeSolo
                                options={catalogos.partes}
                                getOptionLabel={(opcion) => (typeof opcion === "string" ? opcion : opcion?.numero_parte || "")}
                                renderOption={(props, opcion) => (
                                    <li {...props} key={opcion.numero_parte}>
                                        {opcion.numero_parte}
                                        {opcion.nombre_parte ? ` — ${opcion.nombre_parte}` : ""}
                                    </li>
                                )}
                                inputValue={numeroParte}
                                onInputChange={(_, newInputValue) => setNumeroParte(newInputValue)}
                                onChange={(_, nuevaParte) => onSeleccionarParteDelCatalogo(nuevaParte)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label="Parte"
                                        placeholder="Buscar o escribir un número de parte nuevo..."
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* Nombre de Parte: se precarga al elegir la Parte, editable. */}
                    <div className="ram-field">
                        <div className="ram-field__icon">
                            <DescriptionIcon />
                        </div>
                        <div className="ram-field__content">
                            <TextField
                                size="small"
                                fullWidth
                                required
                                label="Nombre de Parte"
                                placeholder="Nombre de la parte..."
                                value={nombreParte}
                                onChange={({ target }) => setNombreParte(target.value)}
                            />
                        </div>
                    </div>

                    {/* Línea: se precarga al elegir la Parte, editable. */}
                    <div className="ram-field">
                        <div className="ram-field__icon">
                            <LinearScaleIcon />
                        </div>
                        <div className="ram-field__content">
                            <TextField
                                size="small"
                                fullWidth
                                required
                                label="Línea"
                                placeholder="Línea..."
                                value={linea}
                                onChange={({ target }) => setLinea(target.value)}
                            />
                        </div>
                    </div>

                    {/* Familia: freeSolo, admite elegir del catálogo o escribir una nueva. */}
                    <div className="ram-field">
                        <div className="ram-field__icon">
                            <CategoryIcon />
                        </div>
                        <div className="ram-field__content">
                            <Autocomplete
                                size="small"
                                freeSolo
                                options={catalogos.familias}
                                getOptionLabel={(opcion) => (typeof opcion === "string" ? opcion : opcion?.descripcion || "")}
                                inputValue={familia}
                                onInputChange={(_, newInputValue) => setFamilia(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label="Familia"
                                        placeholder="Buscar o escribir la Familia..."
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* Sub Familia 1: freeSolo, obligatoria (2026-07-30). */}
                    <div className="ram-field">
                        <div className="ram-field__icon">
                            <LabelIcon />
                        </div>
                        <div className="ram-field__content">
                            <Autocomplete
                                size="small"
                                freeSolo
                                options={catalogos.subfamilias1}
                                getOptionLabel={(opcion) => (typeof opcion === "string" ? opcion : opcion?.descripcion || "")}
                                inputValue={subFamilia1}
                                onInputChange={(_, newInputValue) => setSubFamilia1(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label="Sub Familia 1"
                                        placeholder="Buscar o escribir la Sub Familia 1..."
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* Sub Familia 2: freeSolo, obligatoria (2026-07-30). */}
                    <div className="ram-field">
                        <div className="ram-field__icon">
                            <LocalOfferIcon />
                        </div>
                        <div className="ram-field__content">
                            <Autocomplete
                                size="small"
                                freeSolo
                                options={catalogos.subfamilias2}
                                getOptionLabel={(opcion) => (typeof opcion === "string" ? opcion : opcion?.descripcion || "")}
                                inputValue={subFamilia2}
                                onInputChange={(_, newInputValue) => setSubFamilia2(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label="Sub Familia 2"
                                        placeholder="Buscar o escribir la Sub Familia 2..."
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="ram-footer">
                    <button
                        type="button"
                        className="ram-btn ram-btn--primary"
                        onClick={onGuardar}
                        disabled={loader.mostrar}
                    >
                        <SaveIcon />
                        Guardar
                    </button>
                </div>
            </PlantillaScreen>
        </div>
    );
};

export default FamiliaRefaccionesModal;
